/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 * Copyright © 2020 ООО АДАПТ info@acfs.spb.ru
 *
 * You may use, distribute and modify this code under the
 * terms of the ООО АДАПТ license
 */

import { useToast } from '@builddocs/system'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import {
  AddMRFRequest,
  AddMRFResponse,
  InfoAboutMRFResponse,
  MRFListResponse,
  ReconcilationMRFAndCertificateRequest,
  ReconcilationMRFAndCertificateResponse,
  // RegisterValidationMRFRequest,
  RegisterValidationMRFResponse,
  StatusAboutMRFResponse,
  StatusInternalValidationMRFRequest,
  StatusInternalValidationMRFResponse,
  StatusValidationMRFResponse,
  UUID,
} from '../../models'
import { tracerError } from '../../sideEffects.container'
import { RestApiPoaService } from '../RestApiPoaService'

export const usePostMRF = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ userId, mrf }: { userId: UUID; mrf: AddMRFRequest }) => {
      const formData = new FormData()

      formData.append(
        'addPoaRequest',
        new Blob([JSON.stringify(mrf.addPoaRequest)], {
          type: 'application/json',
        }),
      )
      formData.append('poaFile', mrf.poaFile)
      formData.append('signatureFile', mrf.signatureFile)

      return RestApiPoaService.post<AddMRFResponse>(
        `/for-user/${userId}`,
        formData,
      )
    },
    onSuccess: () => {
      queryClient.refetchQueries(['mrfList'])
      toast.success('Доверенность успешно добавлена')
    },
  })
}

export const useGetMRFList = ({
  userId,
  enabled = true,
}: {
  userId: UUID
  enabled: boolean
}) => {
  // const { toast } = useToast()

  return useQuery({
    queryKey: ['mrfList'],
    queryFn: () =>
      RestApiPoaService.get<MRFListResponse>(`/for-user/${userId}`).then(
        res => res.data,
      ),
    enabled: enabled,
    onError: (error: any) => {
      tracerError.error(error, {
        userId: userId,
        severity: 'warning',
        component: 'useGetMRFList',
      })
      // const errorMessage =
      //   error.response?.data?.errorDescription ||
      //   error.message ||
      //   'Ошибка при загрузке доверенностей'
      //
      // toast.error(errorMessage)
    },
    select: data => {
      if (data && !('poas' in data)) {
        return null
      }
      return data
    },
  })
}

// Получение массива доверенностей по списку внутренних идентификаторов (poaIdInternal)
export const useGetMRFsByIds = ({
  ids,
  enabled = true,
}: {
  ids: string[]
  enabled?: boolean
}) => {
  type InfoAboutMRFWithValidationResponse = InfoAboutMRFResponse & {
    validationLastUpdated: string
  }

  return useQuery<InfoAboutMRFWithValidationResponse[]>({
    queryKey: ['mrfByIds', [...ids].sort()],
    queryFn: () =>
      RestApiPoaService.post('/poas', { ids }).then(
        (res: any) => res.data || [],
      ),
    enabled: enabled && ids.length > 0,
    onError: (error: any) => {
      tracerError.error(error, {
        severity: 'warning',
        component: 'useGetMRFsByIds',
      })
    },
    select: data => data ?? [],
  })
}

export const useGetInfoAboutMRF = (poaId: string) => {
  const { toast } = useToast()

  return useQuery<InfoAboutMRFResponse, Error>({
    queryKey: ['mrfInfo', poaId],
    queryFn: () =>
      RestApiPoaService.get(`/${poaId}`).then(res => res.data || {}),
    onSuccess: () => {
      toast.success('Доверенность успешно загружена')
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.errorDescription ||
        error.message ||
        'Ошибка при загрузке доверенности'

      toast.error(errorMessage)
    },
  })
}

export const useGetStatusAboutMRF = (poaId: string) => {
  const { toast } = useToast()

  return useQuery<StatusAboutMRFResponse, Error>({
    queryKey: ['mrfStatus', poaId],
    queryFn: () =>
      RestApiPoaService.get(`/${poaId}/status`).then(res => res.data || {}),
    onSuccess: () => {
      toast.success('Статус доверенности успешно загружен')
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.errorDescription ||
        error.message ||
        'Ошибка при загрузке статуса доверенности'

      toast.error(errorMessage)
    },
  })
}

export const useDeleteMRF = ({ poaId }: { poaId: string }) => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation<void, Error, void>({
    mutationFn: () => RestApiPoaService.delete(`/${poaId}`),
    onSuccess: () => {
      queryClient.refetchQueries(['mrfList'])
      toast.success('Доверенность успешно удалена')
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.errorDescription ||
        error.message ||
        'Ошибка при удалении доверенности'

      toast.error(errorMessage)
    },
  })
}

export const usePostReconcilationMRFAndCertificate = ({
  poaId,
  certificate,
}: {
  poaId: string
  certificate: ReconcilationMRFAndCertificateRequest
}) => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation<ReconcilationMRFAndCertificateResponse, Error, void>({
    mutationFn: () => RestApiPoaService.post(`/${poaId}/revise`, certificate),
    onSuccess: () => {
      queryClient.invalidateQueries(['mrfStatus', poaId])
      toast.success('Доверенность успешно зарегистрирована')
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.errorDescription ||
        error.message ||
        'Ошибка при регистрации доверенности'

      toast.error(errorMessage)
    },
  })
}

export const usePostRegisterValidationMRF = ({ poaId }: { poaId: string }) => {
  const queryClient = useQueryClient()

  return useMutation<RegisterValidationMRFResponse, Error, void>({
    mutationFn: () => RestApiPoaService.post(`/validation`, { poaId }),
    onSuccess: () => {
      queryClient.refetchQueries(['mrfList'])
    },
  })
}

export const useGetStatusValidationMRF = (operationId: string) => {
  const { toast } = useToast()

  return useQuery<StatusValidationMRFResponse, Error>({
    queryKey: ['validationStatus', operationId],
    queryFn: () =>
      RestApiPoaService.get(`validation/${operationId}`).then(
        res => res.data || {},
      ),
    onSuccess: () => {
      toast.success('Статус доверенности успешно загружен')
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.errorDescription ||
        error.message ||
        'Ошибка при загрузке статуса валидации доверенности'

      toast.error(errorMessage)
    },
  })
}

export const usePostStatusInternalValidationMRF = () => {
  const { toast } = useToast()

  return useMutation<
    StatusInternalValidationMRFResponse,
    Error,
    StatusInternalValidationMRFRequest
  >({
    mutationFn: (data: StatusInternalValidationMRFRequest) =>
      RestApiPoaService.post(`internal-validation`, data).then(res => res.data),
    onSuccess: () => {
      toast.success('Статус доверенности успешно получен')
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.errorDescription ||
        error.message ||
        'Ошибка при получении статуса валидации доверенности'

      toast.error(errorMessage)
    },
  })
}
